var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import axios from "axios";
import { getGlassDimensions, getRebateDimensions } from "./frameDesigner";
/**
 * Get the price of the frame rim
 * @param sku SKU of the moulding
 * @param rebateSize Rebate size of the frame
 * @returns Price of the frame rim only
 */
export function getFramePrice(sku, rebateSize) {
    return __awaiter(this, void 0, void 0, function* () {
        const { data } = yield axios
            .get(`/api/pricing/frame/frameRim`, {
            params: {
                sku,
                rebateWidth: rebateSize.width,
                rebateLength: rebateSize.length
            }
        });
        return data;
    });
}
export function getWholesaleSheetPrice(sku, quantity = 1) {
    return __awaiter(this, void 0, void 0, function* () {
        const { data } = yield axios
            .get('/api/pricing/sheets/wholesale', {
            params: {
                sku,
                quantity
            }
        });
        return data;
    });
}
/**
 * Get the price of a sheet
 * @param sku SKU of the sheet
 * @param size External size of the sheet
 * @param inFrame Whether the sheet is part of a frame
 * @returns Price of the sheet
 */
export function getSheetPrice(sku, size, inFrame = false, valueGroupID = null, quantity = 1) {
    return __awaiter(this, void 0, void 0, function* () {
        const { data } = yield axios
            .get(`/api/pricing/sheets/${inFrame ? 'inFrame' : 'cutToSize'}`, {
            params: {
                sku,
                width: size.width,
                length: size.length,
                valueGroupID,
                quantity
            }
        });
        return data;
    });
}
export function getUnitPrice(sku, packSize, quantity = 1) {
    return __awaiter(this, void 0, void 0, function* () {
        const { data } = yield axios.get('/api/pricing/units/price', {
            params: {
                sku,
                quantity,
                packSize
            }
        });
        return data;
    });
}
export function getWholesaleUnitPrice(sku, quantity = 1) {
    return __awaiter(this, void 0, void 0, function* () {
        const { data } = yield axios.get('/api/pricing/units/wholesale', {
            params: {
                sku,
                quantity
            }
        });
        return data;
    });
}
export function getLengthPrice(sku, packType = 'length', quantity = 1) {
    return __awaiter(this, void 0, void 0, function* () {
        const { data } = yield axios.get('/api/pricing/lengths/price', {
            params: {
                sku,
                type: packType,
                quantity
            }
        });
        return data;
    });
}
export function getRollPrice(sku, length, quantity = 1) {
    return __awaiter(this, void 0, void 0, function* () {
        const { data } = yield axios.get('/api/pricing/rolls/price', {
            params: {
                sku,
                quantity,
                length,
            }
        });
        return data;
    });
}
/**
 * Get price for standard fixings for a frame
 * @param size Size of the frame being made
 * @returns Price for 2 D-rings and string for a frame
 */
export function getFixingsPrice(size) {
    return __awaiter(this, void 0, void 0, function* () {
        const { data } = yield axios
            .get(`/api/pricing/frame/fixings`, { params: size });
        return data;
    });
}
/**
 * Get the price of a spacer in a frame
 * @param size Size of the frame being made
 * @returns Price of a spacer for a frame
 */
export function getSpacerPrice(size) {
    return __awaiter(this, void 0, void 0, function* () {
        const { data } = yield axios
            .get(`/api/pricing/frame/spacer`, { params: size });
        return data;
    });
}
/**
 * Get the price for a mount
 * @param size External size of the mount
 * @param topMount SKU of the top mount
 * @param bottomMount SKU of the bottom mount
 * @param inFrame Whether the mount is in a frame
 * @returns Price of a single/double mount
 */
export function getMountPrice(size, topMount, bottomMount = null, inFrame = false) {
    return __awaiter(this, void 0, void 0, function* () {
        const { data } = yield axios
            .get(`/api/pricing/mountboard/${inFrame ? 'inFrame' : 'CTS'}`, {
            params: {
                topSku: topMount,
                width: size.width,
                length: size.length,
                bottomSku: bottomMount
            }
        });
        return data;
    });
}
/**
 * Get the breakdown of pricing for a bespoke frame item
 * @param item Bespoke frame item
 * @returns Breakdown of each of the aspects of a bespoke frame
 */
export function getBespokeFramePriceBreakdown(item) {
    var _a, _b;
    return __awaiter(this, void 0, void 0, function* () {
        const promises = [];
        const rebateDimensions = getRebateDimensions(item.size, item.mount.type != 'none' ? item.mount.borders : null);
        const glassDimensions = getGlassDimensions(rebateDimensions);
        const prices = {
            frame: { price: '0.00', exVatPrice: '0.00' },
            mount: { price: '0.00', exVatPrice: '0.00' },
            glazing: { price: '0.00', exVatPrice: '0.00' },
            backing: { price: '0.00', exVatPrice: '0.00' },
            undermount: { price: '0.00', exVatPrice: '0.00' },
            fastmount: { price: '0.00', exVatPrice: '0.00' },
            fixings: { price: '0.00', exVatPrice: '0.00' },
            spacer: { price: '0.00', exVatPrice: '0.00' }
        };
        if (item.moulding) {
            promises.push(getFramePrice(item.moulding.sku, rebateDimensions)
                .then(price => prices.frame = price));
        }
        if (item.glazing) {
            promises.push(getSheetPrice(`G-${item.glazing.id}`, glassDimensions, true)
                .then(price => prices.glazing = {
                price: price.base_unit_price,
                exVatPrice: price.ex_vat_base_unit_price
            }));
        }
        if (item.extras.backing) {
            promises.push(getSheetPrice(`B-17`, glassDimensions, true)
                .then(price => prices.backing = {
                price: price.base_unit_price,
                exVatPrice: price.ex_vat_base_unit_price
            }));
        }
        if (item.extras.undermount) {
            promises.push(getSheetPrice(`UM-61`, glassDimensions, true)
                .then(price => prices.undermount = {
                price: price.base_unit_price,
                exVatPrice: price.ex_vat_base_unit_price
            }));
        }
        if (item.extras.fastmount) {
            promises.push(getSheetPrice(`FM-58`, glassDimensions, true)
                .then(price => prices.fastmount = {
                price: price.base_unit_price,
                exVatPrice: price.ex_vat_base_unit_price
            }));
        }
        if (item.extras.fixings) {
            promises.push(getFixingsPrice(rebateDimensions)
                .then(price => prices.fixings = {
                price: price.base_unit_price,
                exVatPrice: price.ex_vat_base_unit_price
            }));
        }
        if (item.extras.spacer) {
            promises.push(getSpacerPrice(rebateDimensions)
                .then(price => prices.spacer = {
                price: price.base_unit_price,
                exVatPrice: price.ex_vat_base_unit_price
            }));
        }
        if (item.mount.type != 'none') {
            promises.push(getMountPrice({ width: glassDimensions.width - 6, length: glassDimensions.length - 6 }, `MCTS-${(_a = item.mount.top) === null || _a === void 0 ? void 0 : _a.stock_lookup_sku}`, item.mount.type == 'double' ? `MCTS-${(_b = item.mount.bottom) === null || _b === void 0 ? void 0 : _b.stock_lookup_sku}` : null, true).then(price => prices.mount = price));
        }
        yield Promise.all(promises);
        return prices;
    });
}
/**
 * Get the volume discounts for a model product
 * @param productId ID of the model product
 * @param exVatPrice Base Ex VAT Unit Price
 * @returns
 */
export function getProductDiscounts(productId, exVatPrice) {
    return __awaiter(this, void 0, void 0, function* () {
        const { data } = yield axios.get('/api/accessories/productDiscounts', {
            params: {
                productID: productId,
                unitPrice: exVatPrice.replace(',', '')
            }
        });
        return data;
    });
}
